// SEQ-180 follow-up (14 September 2026): what a user-built chart SAYS about a
// bucket, as a pure module so it can be proven under Deno and so the chart
// components stay components (react-refresh/only-export-components).
//
// THE DEFECT THIS FIXES. Every generic chart passed Recharts a `formatter`
// only. A `formatter` is handed the value and the series name, never the
// bucket, so a widget grouped by project showed "$142,880.00" and "Net profit"
// with no project attached to either. The reader had to guess which bar they
// were hovering. The fix is a tooltip MODEL: the bucket first, the grouping
// that produced it second, then one row per series, each formatted by its own
// unit.
//
// UNITS ARE PER SERIES, NOT PER CHART. A scatter plots two different metrics,
// and a line chart can carry a secondary metric with a different unit again.
// Resolving the unit from the series key is what keeps a dollar sign off an
// hours figure, which SEQ-182 requires and which this client reads closely.

import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { groupByExplainer } from "./groupByVocabulary";

/** The bucket keys a generic chart draws series from. */
export type WidgetSeriesKey = "value" | "secondary" | "comparison";

export interface WidgetTooltipSeries {
  /** The series name already shown in the legend, e.g. "Net profit" or "Previous year". */
  name: string;
  value: number | null;
  unit: PnlUnit;
  /** The swatch colour, so the row is tied to the mark the pointer is on. */
  colour: string;
}

export interface WidgetTooltipRow {
  name: string;
  /** Already formatted for its unit, so the renderer makes no formatting decisions. */
  value: string;
  colour: string;
}

export interface WidgetTooltipModel {
  /** The bucket itself: the project name, the month, the builder. */
  title: string;
  /** Why this bucket exists, e.g. "Grouped by project". */
  grouping: string;
  rows: WidgetTooltipRow[];
}

/**
 * Shown when a bucket arrives with no label at all. Never an empty heading: a
 * tooltip whose first line is blank reads as a rendering fault, and the whole
 * point of this module is that the reader knows what they are looking at.
 */
export const UNLABELLED_BUCKET = "Unnamed group";

/** The unit a series is measured in, read from the bucket key it draws. */
export function resolveSeriesUnit(key: WidgetSeriesKey, unit: PnlUnit, secondaryUnit: PnlUnit): PnlUnit {
  // A comparison is the SAME metric in an earlier period, so it shares the
  // primary unit. Only the secondary metric can differ.
  return key === "secondary" ? secondaryUnit : unit;
}

/**
 * The tooltip a generic chart shows for one bucket.
 *
 * A null value still gets a row, rendered by `formatMetricValue` as "N/A".
 * Dropping it would leave a comparison series looking like it was never asked
 * for, when what actually happened is that the prior period had no data.
 */
export function buildWidgetTooltipModel(
  groupBy: string,
  bucketLabel: string | null | undefined,
  series: WidgetTooltipSeries[],
): WidgetTooltipModel {
  const label = (bucketLabel ?? "").trim();
  return {
    title: label.length > 0 ? label : UNLABELLED_BUCKET,
    grouping: groupByExplainer(groupBy).tooltipLine,
    rows: series.map((entry) => ({
      name: entry.name,
      value: formatMetricValue(entry.value, entry.unit),
      colour: entry.colour,
    })),
  };
}

/** The longest axis tick this page draws before it starts to shorten names. */
export const AXIS_LABEL_MAX = 16;

/**
 * A category axis tick, shortened so long project names stay legible instead of
 * overlapping their neighbours.
 *
 * Shortening is only safe BECAUSE the tooltip now carries the full label: the
 * axis is the glance and the tooltip is the answer. The single-character
 * ellipsis is deliberate, since three dots cost three characters of a very
 * tight axis.
 */
export function truncateAxisLabel(label: string, max: number = AXIS_LABEL_MAX): string {
  if (max <= 1) return label.slice(0, Math.max(max, 0));
  if (label.length <= max) return label;
  return `${label.slice(0, max - 1).trimEnd()}…`;
}

/**
 * The pixel width one category needs before a chart is squashed rather than
 * read. Below this the chart scrolls sideways inside its card, which keeps the
 * bars readable on a phone instead of shaving them to hairlines.
 */
export const MIN_CATEGORY_WIDTH = 44;

/** The drawing width a category chart needs for `count` buckets. */
export function categoryChartMinWidth(count: number): number {
  return Math.max(count, 0) * MIN_CATEGORY_WIDTH;
}
